import { useEffect, useState } from "react";

/**
 * Get local storage value (static)
 * @param {string} key Local storage key
 * @returns {T | null} Parsed JSON object of type T, string, or null
 */
export const getLocalStorageValue = <T>(key: string): T | null => {
    const item = localStorage.getItem(key);

    if (item === null) return null;

    try {
        return JSON.parse(item);
    } catch {
        return item as T;
    }
};

/**
 * Update local storage with/without disptach event
 * @param {string} key Local storage key
 * @param {string} value Local storage value
 * @param {boolean} withEvent Dispatch event that local storage is updated
 */
export const updateLocalStorage = ({
    key,
    value,
    withEvent = false,
}: {
    key: string;
    value: string;
    withEvent?: boolean;
}) => {
    localStorage.setItem(key, value);
    withEvent && window.dispatchEvent(new Event("localStorageUpdate"));
};

/**
 * Get local storage key value when it changes
 * @param {string} key Local storage key
 * @returns {T | null} UPDATED local storage value - Parsed JSON object of type T, string, or null
 */
const useLocalStorageListener = <T>(key: string): T | null => {
    const [, forceUpdate] = useState(0); // Force re-render when value change

    useEffect(() => {
        const triggerRender = () => forceUpdate((prev) => prev + 1); // Force re-render

        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === key) {
                triggerRender();
            }
        };

        const _triggerRender = () => triggerRender();

        window.addEventListener("storage", handleStorageChange);
        window.addEventListener("localStorageUpdate", _triggerRender);

        return () => {
            window.removeEventListener("storage", handleStorageChange);
            window.removeEventListener("localStorageUpdate", _triggerRender);
        };
    }, [key]);

    return getLocalStorageValue(key);
};

export default useLocalStorageListener;
